import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './supabase';
import { Plus, Pause, Play, Edit3, ExternalLink, X, Loader2, Image, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { DataGrid } from './ui/DataGrid';
import { Thumb } from './ui/Thumb';
import { cn } from './ui/Table';
// Componentes de uiarc.dev (ver src/ui/arc)
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { Textarea } from './ui/arc/textarea';
import { Checkbox } from './ui/arc/checkbox';
import { MetricCard } from './ui/arc/metric-card';
import { ChipGroup } from './ui/arc/chip-group';
import { Dialog, DialogContent } from './ui/arc/dialog';
import SegmentedControl from './ui/arc/segmented-control';

// Publicaciones de MercadoLibre (crear / editar / pausar, con variantes).
// UI: patrón de src/Insumos.tsx — todo dentro de `.tw` (tokens + preflight acotado de
// uiarc, ver src/ui/arc/foundation.css) y armado con componentes de src/ui y src/ui/arc.

interface MeliItem {
  id: string;
  title: string;
  price: number;
  available_quantity: number;
  sold_quantity: number;
  status: string;
  permalink: string;
  thumbnail: string;
  category_id: string;
  condition: string;
  listing_type_id: string;
  date_created: string;
}

interface CategoryPrediction {
  id: string;
  name: string;
  path: string;
  domain_name: string;
}

interface MeliListingsProps {
  showSnackbar: (message: string, type: 'success' | 'error') => void;
}

const PAGE = 50;
const STATUS_LABEL: Record<string, string> = { active: 'Activas', paused: 'Pausadas', closed: 'Cerradas' };
const STATUS_ONE: Record<string, string> = { active: 'Activa', paused: 'Pausada', closed: 'Cerrada' };

// Estilos de los controles nativos que quedan (select con muchas opciones dinámicas de ML,
// inputs chicos de la tabla de variantes). Mismas medidas y foco que el `input` de uiarc
// (src/ui/arc/input.module.css): uiarc no trae un select nativo y su `select` (Radix) no
// escala a listas de valores de atributos de ML con decenas de opciones.
const fieldCls = 'h-[var(--control-height-md)] w-full min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 '
  + 'text-[length:var(--text-sm)] text-ink transition-colors placeholder:text-faint hover:border-[var(--foreground)] focus:border-[var(--foreground)] focus:outline-none';
const cellFieldCls = 'h-8 w-full min-w-[5rem] rounded-[var(--radius-control)] border border-line bg-card px-2 '
  + 'text-[length:var(--text-sm)] text-ink placeholder:text-faint focus:border-[var(--foreground)] focus:outline-none';
const labelCls = 'mb-2 block text-[length:var(--text-sm)] font-medium text-ink';

const formatPrice = (price: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(price);
const https = (url: string) => (url ? url.replace('http:', 'https:') : url);

type Flag = 'sin_stock' | 'sin_ventas';

export function MeliListings({ showSnackbar }: MeliListingsProps) {
  const [items, setItems] = useState<MeliItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [flag, setFlag] = useState<Flag | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MeliItem | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editStock, setEditStock] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke('meli-listings', {
        body: { action: 'list', status: statusFilter, offset, limit: PAGE },
      });

      if (res.error) throw res.error;
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err: any) {
      console.error('Error fetching listings:', err);
      showSnackbar(`Error cargando publicaciones: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, offset, showSnackbar]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleToggleStatus = async (item: MeliItem) => {
    const newStatus = item.status === 'active' ? 'paused' : 'active';
    setToggling(item.id);
    try {
      const { data, error } = await supabase.functions.invoke('meli-listings', {
        body: { action: 'update', item_id: item.id, status: newStatus },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      showSnackbar(`Publicación ${newStatus === 'active' ? 'activada' : 'pausada'}`, 'success');
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    } catch (err: any) {
      showSnackbar(`Error: ${err.message}`, 'error');
    } finally {
      setToggling(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      const updateBody: any = { item_id: editingItem.id };
      if (editPrice && parseFloat(editPrice) !== editingItem.price) updateBody.price = parseFloat(editPrice);
      if (editStock && parseInt(editStock) !== editingItem.available_quantity) updateBody.stock = parseInt(editStock);
      if (editTitle && editTitle !== editingItem.title) updateBody.title = editTitle;

      const { data, error } = await supabase.functions.invoke('meli-listings', {
        body: { action: 'update', ...updateBody },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      showSnackbar('Publicación actualizada', 'success');
      setItems(prev => prev.map(i => i.id === editingItem.id ? {
        ...i,
        price: updateBody.price || i.price,
        available_quantity: updateBody.stock ?? i.available_quantity,
        title: updateBody.title || i.title,
      } : i));
      setEditingItem(null);
    } catch (err: any) {
      showSnackbar(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item: MeliItem) => {
    setEditingItem(item);
    setEditPrice(String(item.price));
    setEditStock(String(item.available_quantity));
    setEditTitle(item.title);
  };

  // Resumen y chips: sobre la página cargada (la API pagina de a 50).
  const stats = useMemo(() => ({
    sinStock: items.filter(i => i.available_quantity === 0).length,
    sinVentas: items.filter(i => i.sold_quantity === 0).length,
    unidades: items.reduce((s, i) => s + (i.available_quantity || 0), 0),
    vendidas: items.reduce((s, i) => s + (i.sold_quantity || 0), 0),
  }), [items]);
  const paged = total > PAGE;
  const scope = paged ? ' (esta página)' : '';

  const rows = useMemo(() => items.filter(i =>
    flag === 'sin_stock' ? i.available_quantity === 0 : flag === 'sin_ventas' ? i.sold_quantity === 0 : true,
  ), [items, flag]);

  const statusLabel = STATUS_LABEL[statusFilter]?.toLowerCase() || '';

  return (
    <div className="tw w-full min-w-0 pb-12 pt-1">
      {/* ── Encabezado ─────────────────────────────────────────────────────── */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Publicaciones ML</h1>
          <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
            Precio, stock y estado de tus publicaciones en MercadoLibre. Pausar, activar o editar impacta al instante en ML.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl label="Estado" value={statusFilter}
            onValueChange={v => { setStatusFilter(v); setOffset(0); setFlag(null); }}
            options={Object.entries(STATUS_LABEL).map(([value, label]) => ({
              value, label,
              // la API solo devuelve el total del estado pedido: el conteo va en el seleccionado
              accessory: value === statusFilter && !loading ? <Badge size="sm">{total}</Badge> : undefined,
            }))} />
          <Button onClick={() => setShowCreateModal(true)}><Plus size={16} /> Nueva publicación</Button>
        </div>
      </header>

      {/* ── Resumen ─────────────────────────────────────────────────────── */}
      <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard label={`Publicaciones ${statusLabel}`} value={loading ? 0 : total} locale="es-AR"
          context={paged ? `Se ven de a ${PAGE}: ${offset + 1}–${Math.min(offset + PAGE, total)}` : 'Total en MercadoLibre'} />
        <MetricCard label="Sin stock" value={stats.sinStock} locale="es-AR"
          context={`No se pueden comprar hasta reponer${scope}`} />
        <MetricCard label="Unidades en stock" value={stats.unidades} locale="es-AR"
          context={`Suma del stock disponible${scope}`} />
        <MetricCard label="Vendidas" value={stats.vendidas} locale="es-AR"
          context={`Histórico de estas publicaciones${scope}`} />
      </section>

      <DataGrid
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        pageSize={PAGE}
        searchPlaceholder="Buscar publicación o ID…"
        emptyTitle={`No hay publicaciones ${statusLabel}`}
        emptyText={statusFilter === 'active' ? 'Publicá un producto en MercadoLibre desde acá.' : 'Probá con otro estado.'}
        emptyAction={statusFilter === 'active'
          ? <Button size="sm" onClick={() => setShowCreateModal(true)}><Plus size={14} /> Nueva publicación</Button>
          : undefined}
        filtersActive={flag != null}
        onClearFilters={() => setFlag(null)}
        toolbar={
          <ChipGroup label="Filtros rápidos" multiple={false}
            value={flag ? [flag] : []}
            onValueChange={v => setFlag((v[0] as Flag) ?? null)}
            options={[
              { value: 'sin_stock', label: `Sin stock · ${stats.sinStock}` },
              { value: 'sin_ventas', label: `Sin ventas · ${stats.sinVentas}` },
            ]} />
        }
        // Paginado del lado de ML (de a 50): el del grid queda en una sola página.
        footer={
          <div className="flex flex-wrap items-center gap-2">
            <span>
              {rows.length} {rows.length === 1 ? 'publicación' : 'publicaciones'}
              {paged && <> · {offset + 1}–{Math.min(offset + PAGE, total)} de {total}</>}
            </span>
            {paged && <>
              <Button variant="ghost" size="sm" disabled={offset === 0 || loading} aria-label="Página anterior"
                onClick={() => setOffset(o => Math.max(0, o - PAGE))}><ChevronLeft size={16} /></Button>
              <Button variant="ghost" size="sm" disabled={offset + PAGE >= total || loading} aria-label="Página siguiente"
                onClick={() => setOffset(o => o + PAGE)}><ChevronRight size={16} /></Button>
            </>}
          </div>
        }
        cols={[
          { key: 'foto', header: '', width: 'w-16',
            render: (i) => <Thumb src={https(i.thumbnail)} name={i.title} size="md" /> },
          { key: 'titulo', header: 'Publicación', sort: (i) => `${i.title} ${i.id}`,
            render: (i) => (
              <div className="min-w-[14rem] max-w-[34rem]">
                <div className="truncate font-medium text-ink" title={i.title}>{i.title}</div>
                <div className="text-[length:var(--text-xs)] tabular-nums text-faint">{i.id}</div>
              </div>
            ) },
          { key: 'estado', header: 'Estado', width: 'w-28', sort: (i) => i.status,
            render: (i) => (
              <Badge size="sm" tone={i.status === 'active' ? 'success' : i.status === 'paused' ? 'warning' : 'neutral'}>
                {STATUS_ONE[i.status] || i.status}
              </Badge>
            ) },
          { key: 'precio', header: 'Precio', align: 'right', width: 'w-32', sort: (i) => i.price,
            render: (i) => <span className="font-medium">{formatPrice(i.price)}</span> },
          { key: 'stock', header: 'Stock', align: 'right', width: 'w-24', sort: (i) => i.available_quantity,
            render: (i) => (
              <span className={cn(i.available_quantity === 0 && 'font-medium text-[var(--warning)]')}>
                {i.available_quantity.toLocaleString('es-AR')}
              </span>
            ) },
          { key: 'vendidos', header: 'Vendidos', align: 'right', width: 'w-24', hideOnMobile: true, sort: (i) => i.sold_quantity,
            render: (i) => <span className={cn(i.sold_quantity === 0 && 'text-faint')}>{i.sold_quantity.toLocaleString('es-AR')}</span> },
          { key: 'acciones', header: '', width: 'w-36', align: 'right',
            render: (i) => (
              <div className="flex items-center justify-end gap-1">
                {i.status !== 'closed' && (
                  <Button variant="ghost" size="sm" loading={toggling === i.id}
                    title={i.status === 'active' ? 'Pausar en ML' : 'Activar en ML'}
                    aria-label={i.status === 'active' ? `Pausar ${i.title}` : `Activar ${i.title}`}
                    onClick={() => handleToggleStatus(i)}>
                    {i.status === 'active' ? <Pause size={15} /> : <Play size={15} />}
                  </Button>
                )}
                <Button variant="ghost" size="sm" title="Editar" aria-label={`Editar ${i.title}`} onClick={() => openEdit(i)}>
                  <Edit3 size={15} />
                </Button>
                {/* Link con la apariencia del Button ghost sm de uiarc (el Button es un <button>,
                    no sirve para navegar a otra pestaña). */}
                <a href={i.permalink} target="_blank" rel="noopener noreferrer" title="Ver en MercadoLibre" aria-label={`Ver ${i.title} en MercadoLibre`}
                  className="inline-flex h-[var(--control-height-sm)] items-center justify-center rounded-[var(--radius-control)] px-3 text-soft transition-colors hover:bg-muted hover:text-ink">
                  <ExternalLink size={15} />
                </a>
              </div>
            ) },
        ]}
      />

      {/* ── Editar publicación ─────────────────────────────────────────────── */}
      <Dialog open={!!editingItem} onOpenChange={o => { if (!o) setEditingItem(null); }}>
        {editingItem && (
          <DialogContent title="Editar publicación" description={`${editingItem.id} · los cambios se publican en MercadoLibre al guardar`}>
            <div className="mb-5 flex items-center gap-3">
              <Thumb src={https(editingItem.thumbnail)} name={editingItem.title} size="lg" />
              <div className="min-w-0 text-[length:var(--text-sm)]">
                <div className="line-clamp-2 font-medium text-ink">{editingItem.title}</div>
                <div className="mt-1 text-faint">{STATUS_ONE[editingItem.status] || editingItem.status} · {editingItem.sold_quantity} vendidas</div>
              </div>
            </div>
            <div className="grid gap-4">
              <Input label="Título" value={editTitle} onChange={e => setEditTitle(e.target.value)} />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Precio (ARS)" type="number" min="1" value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                <Input label="Stock" type="number" min="0" value={editStock} onChange={e => setEditStock(e.target.value)} />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2 border-t border-line pt-4">
              <Button variant="secondary" onClick={() => setEditingItem(null)}>Cancelar</Button>
              <Button loading={saving} onClick={handleSaveEdit}>Guardar</Button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* ── Nueva publicación ──────────────────────────────────────────────── */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        {showCreateModal && (
          <CreateListingModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => { setShowCreateModal(false); fetchListings(); }}
            showSnackbar={showSnackbar}
          />
        )}
      </Dialog>
    </div>
  );
}

// Variation types
interface VariationAttribute {
  id: string;
  name: string;
  values: { id: string; name: string }[];
  value_type: string;
  hierarchy?: string;
  required?: boolean;
}

interface VariationRow {
  attribute_combinations: { id: string; value_name: string; value_id?: string }[];
  price: string;
  stock: string;
  sku: string;
}

// Create Listing Modal (contenido del Dialog de uiarc)
function CreateListingModal({
  onClose,
  onCreated,
  showSnackbar,
}: {
  onClose: () => void;
  onCreated: () => void;
  showSnackbar: (message: string, type: 'success' | 'error') => void;
}) {
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('1');
  const [description, setDescription] = useState('');
  const [condition, setCondition] = useState('new');
  const [listingType, setListingType] = useState('gold_special');
  const [freeShipping, setFreeShipping] = useState(false);
  const [pictures, setPictures] = useState<string[]>(['']);
  const [categoryId, setCategoryId] = useState('');
  const [categoryPath, setCategoryPath] = useState('');
  const [categories, setCategories] = useState<CategoryPrediction[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [saving, setSaving] = useState(false);

  // Variations
  const [hasVariations, setHasVariations] = useState(false);
  const [variationAttrs, setVariationAttrs] = useState<VariationAttribute[]>([]);
  const [selectedVarAttrs, setSelectedVarAttrs] = useState<string[]>([]);
  const [variations, setVariations] = useState<VariationRow[]>([]);
  const [loadingAttrs, setLoadingAttrs] = useState(false);

  // Required attributes (FAMILY, PARENT_PK, PACKAGE)
  const [requiredAttrs, setRequiredAttrs] = useState<VariationAttribute[]>([]);
  const [attrValues, setAttrValues] = useState<Record<string, any>>({});

  const predictCategory = async (searchTitle?: string) => {
    const t = searchTitle || title;
    if (!t || t.length < 3) return;
    setLoadingCategories(true);
    try {
      const { data, error } = await supabase.functions.invoke('meli-listings', {
        body: { action: 'predict_category', title: t },
      });
      if (error) throw error;
      setCategories(data?.categories || []);
    } catch (err) {
      console.error('Category prediction error:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Auto-predict when title changes (debounced)
  useEffect(() => {
    if (!title || title.length < 3 || categoryId) return;
    const timer = setTimeout(() => predictCategory(title), 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, categoryId]);

  // Fetch variation attributes when category is selected
  const fetchCategoryAttributes = async (catId: string) => {
    setLoadingAttrs(true);
    try {
      const { data, error } = await supabase.functions.invoke('meli-listings', {
        body: { action: 'category_attributes', category_id: catId },
      });
      if (error) throw error;
      setVariationAttrs(data?.variation_attributes || []);
      setRequiredAttrs(data?.required_attributes || []);
    } catch (err) {
      console.error('Error fetching category attributes:', err);
    } finally {
      setLoadingAttrs(false);
    }
  };

  const handleSelectCategory = (cat: CategoryPrediction) => {
    setCategoryId(cat.id);
    setCategoryPath(cat.path);
    fetchCategoryAttributes(cat.id);
  };

  const handleClearCategory = () => {
    setCategoryId('');
    setCategoryPath('');
    setVariationAttrs([]);
    setRequiredAttrs([]);
    setAttrValues({});
    setSelectedVarAttrs([]);
    setVariations([]);
    setHasVariations(false);
  };

  const toggleVarAttr = (attrId: string) => {
    setSelectedVarAttrs(prev => {
      const next = prev.includes(attrId) ? prev.filter(a => a !== attrId) : [...prev, attrId];
      // Reset variations when attributes change
      if (next.length > 0) {
        setVariations([{
          attribute_combinations: next.map(id => ({ id, value_name: '', value_id: undefined })),
          price: price || '',
          stock: '1',
          sku: '',
        }]);
      } else {
        setVariations([]);
      }
      return next;
    });
  };

  const addVariationRow = () => {
    setVariations(prev => [...prev, {
      attribute_combinations: selectedVarAttrs.map(id => ({ id, value_name: '', value_id: undefined })),
      price: prev.length > 0 ? prev[prev.length - 1].price : (price || ''),
      stock: '1',
      sku: '',
    }]);
  };

  const removeVariationRow = (index: number) => {
    setVariations(prev => prev.filter((_, i) => i !== index));
  };

  const updateVariation = (index: number, field: string, value: string) => {
    setVariations(prev => prev.map((v, i) => {
      if (i !== index) return v;
      if (field === 'price' || field === 'stock' || field === 'sku') {
        return { ...v, [field]: value };
      }
      return v;
    }));
  };

  const updateVariationAttr = (varIndex: number, attrId: string, valueName: string, valueId?: string) => {
    setVariations(prev => prev.map((v, i) => {
      if (i !== varIndex) return v;
      return {
        ...v,
        attribute_combinations: v.attribute_combinations.map(ac =>
          ac.id === attrId ? { ...ac, value_name: valueName, value_id: valueId } : ac
        ),
      };
    }));
  };

  const handleCreate = async () => {
    if (!title) {
      showSnackbar('Completá el título', 'error');
      return;
    }
    if (!categoryId) {
      showSnackbar('Seleccioná una categoría (escribí el título y elegí una de las sugeridas)', 'error');
      return;
    }

    if (hasVariations && variations.length > 0) {
      const invalid = variations.some(v =>
        !v.price || v.attribute_combinations.some(ac => !ac.value_name)
      );
      if (invalid) {
        showSnackbar('Completá todos los campos de las variantes (atributos y precio)', 'error');
        return;
      }
    } else if (!price) {
      showSnackbar('Completá el precio', 'error');
      return;
    }

    setSaving(true);
    try {
      const validPictures = pictures.filter(p => p.trim());
      const bodyData: any = {
        action: 'create',
        title,
        description,
        condition,
        listing_type: listingType,
        free_shipping: freeShipping,
        category_id: categoryId,
        pictures: validPictures,
        attributes: attrValues,
      };

      if (hasVariations && variations.length > 0) {
        bodyData.variations = variations.map(v => ({
          attribute_combinations: v.attribute_combinations,
          price: parseFloat(v.price),
          stock: parseInt(v.stock) || 1,
          sku: v.sku || undefined,
        }));
      } else {
        bodyData.price = parseFloat(price);
        bodyData.stock = parseInt(stock) || 1;
      }

      const { data, error } = await supabase.functions.invoke('meli-listings', {
        body: bodyData,
      });
      if (error) throw error;
      if (data?.error) throw new Error(JSON.stringify(data.details || data.error));

      showSnackbar(`Publicación creada: ${data.item?.id}`, 'success');
      onCreated();
    } catch (err: any) {
      showSnackbar(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getAttrName = (attrId: string) => variationAttrs.find(a => a.id === attrId)?.name || attrId;
  const getAttrValues = (attrId: string) => variationAttrs.find(a => a.id === attrId)?.values || [];

  const sectionCls = 'border-t border-line pt-5';

  return (
    <DialogContent title="Nueva publicación en MercadoLibre"
      description="Escribí el título, elegí la categoría sugerida y completá precio, stock y fotos."
      className="!w-[min(calc(100vw-2rem),760px)]">
      <div className="grid gap-5">
        {/* ── Título + categoría ── */}
        <div className="grid gap-3">
          <Input label="Título *" value={title} maxLength={60}
            placeholder="Ej: Lámpara de escritorio LED con brazo articulado"
            description={`${title.length}/60 caracteres`}
            onChange={e => setTitle(e.target.value)} />

          {loadingCategories && (
            <div className="flex items-center gap-2 text-[length:var(--text-sm)] text-faint">
              <Loader2 size={14} className="animate-spin" /> Buscando categoría…
            </div>
          )}
          {categories.length > 0 && !categoryId && (
            <div>
              <span className={labelCls}>Categoría sugerida</span>
              <div className="grid gap-1.5">
                {categories.map(cat => (
                  <button key={cat.id} type="button" onClick={() => handleSelectCategory(cat)}
                    className="rounded-[var(--radius-control)] border border-line px-3 py-2 text-left text-[length:var(--text-sm)] text-soft transition-colors hover:border-[var(--foreground)] hover:text-ink">
                    {cat.path}
                  </button>
                ))}
              </div>
            </div>
          )}
          {categoryId && (
            <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-line bg-muted px-3 py-2 text-[length:var(--text-sm)]">
              <span className="text-faint">Categoría</span>
              <span className="min-w-0 flex-1 truncate font-medium text-ink" title={categoryPath}>{categoryPath}</span>
              <Button variant="ghost" size="sm" aria-label="Cambiar categoría" title="Cambiar categoría" onClick={handleClearCategory}><X size={14} /></Button>
            </div>
          )}
          {loadingAttrs && (
            <div className="flex items-center gap-2 text-[length:var(--text-sm)] text-faint">
              <Loader2 size={14} className="animate-spin" /> Cargando atributos…
            </div>
          )}
        </div>

        {/* ── Atributos obligatorios de la categoría ── */}
        {categoryId && requiredAttrs.length > 0 && (
          <div className={sectionCls}>
            <h3 className="mb-3 text-[length:var(--text-sm)] font-semibold text-ink">Atributos del producto</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {requiredAttrs.map(attr => attr.values.length > 0 ? (
                <label key={attr.id} className="block min-w-0">
                  <span className={labelCls}>{attr.name}{attr.required && <span className="text-[var(--danger)]"> *</span>}</span>
                  <select className={fieldCls}
                    value={typeof attrValues[attr.id] === 'object' ? attrValues[attr.id]?.value_id || '' : attrValues[attr.id] || ''}
                    onChange={e => {
                      const selected = attr.values.find(v => v.id === e.target.value);
                      setAttrValues(prev => ({
                        ...prev,
                        [attr.id]: selected ? { value_id: selected.id, value_name: selected.name } : '',
                      }));
                    }}>
                    <option value="">Seleccionar…</option>
                    {attr.values.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </label>
              ) : (
                <Input key={attr.id} label={`${attr.name}${attr.required ? ' *' : ''}`}
                  value={attrValues[attr.id] || ''}
                  onChange={e => setAttrValues(prev => ({ ...prev, [attr.id]: e.target.value }))}
                  placeholder={
                    attr.id.includes('WEIGHT') ? 'Ej: 500 g' :
                    attr.id.includes('HEIGHT') || attr.id.includes('WIDTH') || attr.id.includes('LENGTH') ? 'Ej: 30 cm' :
                    attr.name
                  } />
              ))}
            </div>
          </div>
        )}

        {/* ── Variantes ── */}
        {categoryId && variationAttrs.length > 0 && (
          <div className={sectionCls}>
            <Checkbox label="Publicar con variantes" description="Color, tamaño, etc. Cada variante con su precio, stock y SKU."
              checked={hasVariations}
              onCheckedChange={v => {
                const on = v === true;
                setHasVariations(on);
                if (!on) { setSelectedVarAttrs([]); setVariations([]); }
              }} />

            {hasVariations && (
              <div className="mt-3 grid gap-3">
                <div>
                  <span className={labelCls}>Atributos que cambian entre variantes</span>
                  <ChipGroup label="Atributos de variante"
                    value={selectedVarAttrs}
                    onValueChange={next => {
                      // ChipGroup devuelve la selección nueva: se aplica como un toggle del que cambió.
                      const changed = next.find(id => !selectedVarAttrs.includes(id)) ?? selectedVarAttrs.find(id => !next.includes(id));
                      if (changed) toggleVarAttr(changed);
                    }}
                    options={variationAttrs.map(a => ({ value: a.id, label: a.name }))} />
                </div>

                {selectedVarAttrs.length > 0 && (
                  <div className="overflow-hidden rounded-[var(--radius-panel)] border border-line">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[length:var(--text-sm)]">
                        <thead>
                          <tr className="border-b border-line bg-muted text-left text-faint">
                            {selectedVarAttrs.map(attrId => <th key={attrId} className="h-9 whitespace-nowrap px-2 font-medium">{getAttrName(attrId)}</th>)}
                            <th className="h-9 px-2 font-medium">Precio</th>
                            <th className="h-9 px-2 font-medium">Stock</th>
                            <th className="h-9 px-2 font-medium">SKU</th>
                            <th className="h-9 w-10" />
                          </tr>
                        </thead>
                        <tbody>
                          {variations.map((v, vi) => (
                            <tr key={vi} className="border-b border-[var(--border-subtle)] last:border-0">
                              {selectedVarAttrs.map(attrId => {
                                const values = getAttrValues(attrId);
                                const currentAc = v.attribute_combinations.find(ac => ac.id === attrId);
                                return (
                                  <td key={attrId} className="px-2 py-1.5">
                                    {values.length > 0 ? (
                                      <select className={cellFieldCls} value={currentAc?.value_id || ''}
                                        onChange={e => {
                                          const selected = values.find(val => val.id === e.target.value);
                                          updateVariationAttr(vi, attrId, selected?.name || '', selected?.id);
                                        }}>
                                        <option value="">Elegir…</option>
                                        {values.map(val => <option key={val.id} value={val.id}>{val.name}</option>)}
                                      </select>
                                    ) : (
                                      <input type="text" className={cellFieldCls} value={currentAc?.value_name || ''}
                                        onChange={e => updateVariationAttr(vi, attrId, e.target.value)}
                                        placeholder={getAttrName(attrId)} />
                                    )}
                                  </td>
                                );
                              })}
                              <td className="px-2 py-1.5">
                                <input type="number" className={cn(cellFieldCls, 'text-right tabular-nums')} value={v.price} min="1" placeholder="0"
                                  onChange={e => updateVariation(vi, 'price', e.target.value)} />
                              </td>
                              <td className="px-2 py-1.5">
                                <input type="number" className={cn(cellFieldCls, 'text-right tabular-nums')} value={v.stock} min="1" placeholder="1"
                                  onChange={e => updateVariation(vi, 'stock', e.target.value)} />
                              </td>
                              <td className="px-2 py-1.5">
                                <input type="text" className={cellFieldCls} value={v.sku} placeholder="Opcional"
                                  onChange={e => updateVariation(vi, 'sku', e.target.value)} />
                              </td>
                              <td className="px-1 py-1.5 text-right">
                                {variations.length > 1 && (
                                  <Button variant="ghost" size="sm" aria-label="Quitar variante" onClick={() => removeVariationRow(vi)}>
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="border-t border-line p-2">
                      <Button variant="ghost" size="sm" onClick={addVariationRow}><Plus size={14} /> Agregar variante</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Precio, stock y condiciones ── */}
        <div className={cn('grid gap-4 sm:grid-cols-2', sectionCls)}>
          {!hasVariations && <>
            <Input label="Precio (ARS) *" type="number" min="1" placeholder="0" value={price} onChange={e => setPrice(e.target.value)} />
            <Input label="Stock *" type="number" min="1" value={stock} onChange={e => setStock(e.target.value)} />
          </>}
          <label className="block min-w-0">
            <span className={labelCls}>Condición</span>
            <select className={fieldCls} value={condition} onChange={e => setCondition(e.target.value)}>
              <option value="new">Nuevo</option>
              <option value="used">Usado</option>
            </select>
          </label>
          <label className="block min-w-0">
            <span className={labelCls}>Tipo de publicación</span>
            <select className={fieldCls} value={listingType} onChange={e => setListingType(e.target.value)}>
              <option value="gold_special">Premium (Clásica)</option>
              <option value="gold_pro">Oro Pro</option>
              <option value="gold">Oro</option>
              <option value="silver">Plata</option>
              <option value="bronze">Bronce</option>
              <option value="free">Gratuita</option>
            </select>
          </label>
          <div className="sm:col-span-2">
            <Checkbox label="Envío gratis" checked={freeShipping} onCheckedChange={v => setFreeShipping(v === true)} />
          </div>
        </div>

        {/* ── Descripción y fotos ── */}
        <div className={cn('grid gap-4', sectionCls)}>
          <Textarea label="Descripción" rows={4} placeholder="Descripción del producto…"
            value={description} onChange={e => setDescription(e.target.value)} />

          <div>
            <span className={labelCls}>Fotos (URLs)</span>
            <div className="grid gap-2">
              {pictures.map((pic, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="relative flex min-w-0 flex-1 items-center">
                    <Image size={15} className="pointer-events-none absolute left-3 text-faint" />
                    <input type="text" className={cn(fieldCls, 'pl-9')} value={pic} placeholder="https://ejemplo.com/foto.jpg"
                      onChange={e => {
                        const newPics = [...pictures];
                        newPics[i] = e.target.value;
                        setPictures(newPics);
                      }} />
                  </div>
                  {pictures.length > 1 && (
                    <Button variant="ghost" size="sm" aria-label="Quitar foto" onClick={() => setPictures(pictures.filter((_, j) => j !== i))}>
                      <X size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {pictures.length < 10 && (
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => setPictures([...pictures, ''])}>
                <Plus size={14} /> Agregar foto
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button loading={saving} onClick={handleCreate}>Crear publicación</Button>
      </div>
    </DialogContent>
  );
}
